import { useEffect, useRef, useState } from "react";
import { Music2, Play, Pause, Check, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function TrackRow({ t, selected, playing, onPlay, onPick }) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl p-2.5 ${selected ? "bg-[hsl(var(--gold))]/15 ring-1 ring-[hsl(var(--gold))]/50" : "bg-white/5"}`} data-testid={`music-track-${t.id}`}>
      <button onClick={onPlay} style={{ background: t.color }} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[#0B0F17]" data-testid={`music-preview-${t.id}`} aria-label="Ouvir prévia">
        {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{t.title}</p>
        <p className="truncate text-xs text-muted-foreground">{t.mood} · {t.artist}</p>
      </div>
      <button onClick={onPick} className={`rounded-full px-3 py-1.5 text-xs font-bold ${selected ? "bg-[hsl(var(--gold))] text-[#0B0F17]" : "bg-white/10"}`} data-testid={`music-select-${t.id}`}>
        {selected ? <Check size={14} /> : "Usar"}
      </button>
    </div>
  );
}

export function MusicPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);

  useEffect(() => { if (open && !tracks.length) api.get("/music").then((r) => setTracks(r.data)); }, [open, tracks.length]);
  useEffect(() => { if (!open) { audio.current?.pause(); setPlaying(null); } }, [open]);

  const preview = (t) => {
    if (playing === t.id) { audio.current.pause(); return setPlaying(null); }
    audio.current.src = t.preview_url;
    audio.current.play().catch(() => {});
    setPlaying(t.id);
  };
  const pick = (t) => onChange({ track: t, start: 30, volume: 0.8 });

  return (
    <>
      {value ? (
        <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 p-3" data-testid="selected-music">
          <Music2 size={18} className="shrink-0 text-[hsl(var(--gold))]" />
          <button onClick={() => setOpen(true)} className="min-w-0 flex-1 text-left" data-testid="change-music-button">
            <p className="truncate text-sm font-bold" data-testid="selected-music-title">{value.track.title}</p>
            <p className="truncate text-xs text-muted-foreground">{value.track.artist} · a partir de {fmt(value.start)}</p>
          </button>
          <button onClick={() => onChange(null)} className="grid h-8 w-8 place-items-center rounded-full bg-white/10" data-testid="remove-music-button" aria-label="Remover trilha"><X size={14} /></button>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="flex w-full items-center gap-2 rounded-2xl border border-dashed border-white/20 p-3 text-sm font-bold hover:border-[hsl(var(--gold))]" data-testid="add-music-button">
          <Music2 size={18} className="text-[hsl(var(--gold))]" /> Adicionar Trilha Sonora
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="mx-auto max-h-[85dvh] max-w-lg overflow-y-auto rounded-t-3xl" data-testid="music-picker-sheet">
          <SheetHeader>
            <SheetTitle>Trilha Sonora</SheetTitle>
            <SheetDescription>Faixas instrumentais de exemplo, livres para uso.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {tracks.map((t) => <TrackRow key={t.id} t={t} selected={value?.track.id === t.id} playing={playing === t.id} onPlay={() => preview(t)} onPick={() => pick(t)} />)}
          </div>
          {value && (
            <div className="mt-5 space-y-4 rounded-2xl bg-white/5 p-4" data-testid="music-settings">
              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Começar em {fmt(value.start)}</p>
                <Slider value={[value.start]} min={0} max={Math.max(0, value.track.duration - 30)} step={5} onValueChange={([v]) => onChange({ ...value, start: v })} data-testid="music-start-slider" />
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Volume da trilha {Math.round(value.volume * 100)}%</p>
                <Slider value={[value.volume * 100]} min={10} max={100} step={5} onValueChange={([v]) => onChange({ ...value, volume: v / 100 })} data-testid="music-volume-slider" />
              </div>
              <button onClick={() => setOpen(false)} className="h-11 w-full rounded-full bg-[hsl(var(--gold))] text-sm font-extrabold text-[#0B0F17]" data-testid="music-done-button">Concluir</button>
            </div>
          )}
          <audio ref={audio} onEnded={() => setPlaying(null)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
